// Global Instructions Rule Applied!
//
// Partner Allocation section of the fund's Statements tab
// (docs/LATE_CLOSER_PNL_ALLOCATION_DESIGN.md 4.4, PR 3): the CPA's working
// paper for the K-1s. For a financial year (to an as-of date inside an open
// year) one row per partner x K-1 tax category - the fund total, the
// pro-rata share, the curative part that catches a later closer up, the
// allocation (booked for a closed year, the engine's unbooked figures for
// the open year), the carry-forward, the partner's own stamped lines and
// the management fees among them - with totals per category tied to the
// ledger. One read (view__fund_partner_allocation through
// GET /funds/:id/fin-years/:fyId/partner-allocation); every figure is the
// engine's, nothing is computed here.
//
// Download workbook (the design-pattern rule for the audit file of a
// computed record) fetches the server-built xlsx: the report and the audit
// detail sheet with every allocation line, the population and share used,
// the partner's own lines and the tie to the capital account statement.
//
// Fund mode only: a consolidated fund allocates in its legal entities, so
// the section renders nothing in group mode. Section-local state like the
// Investor Statements section (no redux slice).

import React, { useEffect, useMemo, useState } from 'react'
import { Button, Select, Table, Tag, Tooltip, Typography } from 'antd'
import { DownloadOutlined, ReloadOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import DatePicker from '../../../../../core/components/DatePicker.tsx'
import NumberText from '../../../../../core/components/NumberText.tsx'
import { FormSection } from '../../../../../core/components/FormSection.tsx'
import { showErrorToast } from '../../../../../core/utils/errorToast.tsx'
import { getAccessToken } from '../../../../../core/auth/authService'
import { store } from '../../../../../state_manager/store.ts'
import { downloadFundPartnerAllocationWorkbook, fetchFundPartnerAllocation } from '../../../services/fundsService.ts'
import type {
  FundPartnerAllocation,
  FundPartnerAllocationCategory,
  FundPartnerAllocationRow
} from '../../../../../state_manager/data_models/fund_partner_allocation.ts'

const { Text } = Typography

const DATE_FORMAT = 'MM/DD/YYYY'
const pct = (v: number): string => `${(v * 100).toFixed(2)}%`
const money = (v: number | null | undefined): React.ReactNode => <NumberText value={v} />
const strong = (v: number | null | undefined): React.ReactNode => (
  <NumberText value={v} wrap={(f) => <Text strong>{f}</Text>} />
)

export interface PartnerAllocationFyOption {
  value: string
  label: string
  closed?: boolean
}

interface PartnerAllocationSectionProps {
  fundId: string | null
  /** Consolidated mode: nothing to show (the legal entities allocate). */
  group?: { fundGroupId: string; primaryFundId: string | null }
  /** The tab's financial-year options (fetchFundFinYears): not refetched here. */
  fyOptions: PartnerAllocationFyOption[]
}

/** The year the section opens on: the first open year, else the first. */
export const defaultFy = (options: PartnerAllocationFyOption[]): string | null =>
  options.find((o) => o.closed !== true)?.value ?? options[0]?.value ?? null

export default function PartnerAllocationSection({ fundId, group, fyOptions }: PartnerAllocationSectionProps) {
  const [selectedFy, setSelectedFy] = useState<string | null>(null)
  const [asOf, setAsOf] = useState<string | null>(null)
  const [data, setData] = useState<FundPartnerAllocation | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [downloading, setDownloading] = useState(false)
  // Refresh re-runs the same read
  const [refreshTick, setRefreshTick] = useState(0)

  // the year follows the options until the user picks one
  const fy = selectedFy ?? defaultFy(fyOptions)

  useEffect(() => {
    if (group || !fundId || !fy) {
      setData(null)
      return
    }
    let cancelled = false
    setLoading(true)
    const token = getAccessToken(store.getState().auth)
    void fetchFundPartnerAllocation(fundId, fy, { asOf }, token).then((res) => {
      if (cancelled) return
      setLoading(false)
      setError(res.error ?? null)
      setData(res.data)
    })
    return () => {
      cancelled = true
    }
  }, [fundId, group, fy, asOf, refreshTick])

  const rows: FundPartnerAllocationRow[] = useMemo(() => data?.rows ?? [], [data])
  const categories: FundPartnerAllocationCategory[] = useMemo(() => data?.categories ?? [], [data])
  const totals = data?.totals ?? null

  const handleDownload = async () => {
    if (!fundId || !fy) return
    setDownloading(true)
    try {
      const res = await downloadFundPartnerAllocationWorkbook(fundId, fy, asOf, getAccessToken(store.getState().auth))
      if (!res.success) showErrorToast(res.error || 'Failed to download the workbook')
    } finally {
      setDownloading(false)
    }
  }

  if (group || !fundId) return null

  const categoryCell = (label: string, box: string | null | undefined) => (
    <span>
      {label}
      {box ? (
        <Text type='secondary' className='text-xs ml-1'>
          Box {box}
        </Text>
      ) : null}
    </span>
  )

  const rowColumns = [
    {
      title: 'Partner',
      dataIndex: 'investors__name',
      render: (v: string, row: FundPartnerAllocationRow) => (
        <span>
          {v}{' '}
          <Text type='secondary' className='text-xs'>
            {pct(row.prog__share)}
          </Text>
        </span>
      )
    },
    {
      title: 'Tax category',
      dataIndex: 'tax_categories__label',
      width: 220,
      render: (v: string, row: FundPartnerAllocationRow) => categoryCell(v, row.tax_categories__k1_box)
    },
    { title: 'Fund total', dataIndex: 'prog__fund_total', width: 120, align: 'right' as const, render: money },
    { title: 'Pro rata', dataIndex: 'prog__pro_rata', width: 120, align: 'right' as const, render: money },
    { title: 'Curative', dataIndex: 'prog__curative', width: 110, align: 'right' as const, render: money },
    { title: 'Allocated', dataIndex: 'prog__allocated', width: 120, align: 'right' as const, render: money },
    { title: 'Carry-forward', dataIndex: 'prog__carry_forward', width: 120, align: 'right' as const, render: money },
    { title: 'Mgmt fees', dataIndex: 'prog__management_fees', width: 110, align: 'right' as const, render: money },
    { title: 'Direct', dataIndex: 'prog__direct', width: 110, align: 'right' as const, render: money },
    { title: 'Partner total', dataIndex: 'prog__partner_total', width: 130, align: 'right' as const, render: money }
  ]

  const categoryColumns = [
    {
      title: 'Tax category',
      dataIndex: 'tax_categories__label',
      render: (v: string, row: FundPartnerAllocationCategory) => categoryCell(v, row.tax_categories__k1_box)
    },
    { title: 'Fund total', dataIndex: 'prog__fund_total', width: 130, align: 'right' as const, render: money },
    { title: 'Allocated', dataIndex: 'prog__allocated', width: 130, align: 'right' as const, render: money },
    { title: 'Direct', dataIndex: 'prog__direct', width: 130, align: 'right' as const, render: money },
    { title: 'Ledger total', dataIndex: 'prog__ledger_total', width: 130, align: 'right' as const, render: money },
    { title: 'Difference', dataIndex: 'prog__difference', width: 110, align: 'right' as const, render: money },
    {
      title: '',
      dataIndex: 'prog__tied',
      width: 110,
      render: (tied: boolean, row: FundPartnerAllocationCategory) =>
        tied ? (
          <Tag color='green'>Tied</Tag>
        ) : (
          <Tag color='red'>
            Off by <NumberText value={row.prog__difference} />
          </Tag>
        )
    }
  ]

  const downloadBlocked = rows.length === 0 ? 'Nothing to export' : null

  return (
    <FormSection
      title='Partner Allocation'
      titleColor='blue'
      loading={loading}
      className='mt-3'
      headerActions={
        <div className='flex items-center gap-2'>
          <Select
            size='small'
            style={{ minWidth: 110 }}
            value={fy}
            options={fyOptions}
            placeholder='Financial year'
            aria-label='Financial year'
            onChange={(v) => setSelectedFy(v)}
          />
          <DatePicker
            size='small'
            value={asOf ? dayjs(asOf) : null}
            format={DATE_FORMAT}
            placeholder='As of (FY end)'
            aria-label='As of'
            onChange={(d) => setAsOf(d ? d.format('YYYY-MM-DD') : null)}
          />
          <Button size='small' icon={<ReloadOutlined />} onClick={() => setRefreshTick((t) => t + 1)}>
            Refresh
          </Button>
          <Tooltip title={downloadBlocked ?? ''}>
            <span>
              <Button
                size='small'
                icon={<DownloadOutlined />}
                loading={downloading}
                disabled={downloadBlocked != null}
                onClick={() => void handleDownload()}
              >
                Download workbook
              </Button>
            </span>
          </Tooltip>
        </div>
      }
    >
      {error ? <Text type='danger'>{error}</Text> : null}

      {data ? (
        <div className='mb-2 flex flex-wrap items-center gap-2'>
          <Text type='secondary'>
            FY {data.fin_year.fin_year}
            {data.fin_year.year_start_date
              ? ` (${dayjs(data.fin_year.year_start_date).format(DATE_FORMAT)} – ${dayjs(
                  data.fin_year.window_end ?? data.fin_year.year_end_date
                ).format(DATE_FORMAT)})`
              : ''}
            {` · ${data.basis_label}`}
          </Text>
          {data.is_booked ? <Tag color='green'>Booked</Tag> : <Tag color='blue'>Unbooked</Tag>}
          {data.provisional ? <Tag color='gold'>Provisional</Tag> : null}
          {totals ? totals.prog__tied ? <Tag color='green'>Tied to the ledger</Tag> : <Tag color='red'>Not tied</Tag> : null}
        </div>
      ) : null}

      {data?.status_message ? (
        <Text type='secondary' className='block mb-2'>
          {data.status_message}
        </Text>
      ) : null}

      {rows.length === 0 && !loading && !error ? (
        <Text type='secondary'>No items in this period.</Text>
      ) : rows.length > 0 ? (
        <Table
          size='small'
          columns={rowColumns}
          dataSource={rows}
          pagination={false}
          rowKey={(r) => `${r.investors__id}|${r.prog__cat_key}`}
          summary={() =>
            totals ? (
              <Table.Summary fixed>
                <Table.Summary.Row className='font-medium'>
                  <Table.Summary.Cell index={0} colSpan={5}>
                    Total
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={5} align='right'>
                    {strong(totals.prog__allocated)}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={6} />
                  <Table.Summary.Cell index={7} align='right'>
                    {strong(totals.prog__management_fees)}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={8} align='right'>
                    {strong(totals.prog__direct)}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={9} align='right'>
                    {strong(totals.prog__allocated + totals.prog__direct)}
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              </Table.Summary>
            ) : null
          }
        />
      ) : null}

      {categories.length > 0 ? (
        <>
          <Text type='secondary' className='block mt-3 mb-1'>
            Totals per category
          </Text>
          <Table
            size='small'
            columns={categoryColumns}
            dataSource={categories}
            pagination={false}
            rowKey='prog__cat_key'
            style={{ maxWidth: 1000 }}
          />
        </>
      ) : null}

      <Text type='secondary' className='block mt-2'>
        The fund-level items of the period are allocated per partner - booked for a closed year, the engine&apos;s
        unbooked figures for the open year. Curative is the part that catches a later closer up; Direct lines are
        the partner&apos;s own stamped journals (management fees, charges to one partner), outside the allocation;
        the ledger total is the trial balance&apos;s movement of the category&apos;s accounts before the year-end
        closing entries.
      </Text>
    </FormSection>
  )
}
